import { useEffect, useMemo, useRef, useState, type KeyboardEvent, type ReactNode } from 'react';
import { fetchReferenceLibrary, type BrandReferenceLibrary } from '../../lib/brandLibrary';
import {
  createAdCreatorGenerationEventSourceV2,
  getAdCreatorGenerationResultV2,
  postAdCreatorGenerateFullV2,
  type BrandAssetUsage,
  type GenerationStatus,
  type V2ProgressStepId,
  type V2ProgressUpdate
} from '../../lib/adCreator';

type SpecializationType = 'Persona特式化' | '活動特式化' | '見證代言特式化';

interface Headline {
  id: string;
  text: string;
  charCount?: number;
  brandAssetUsage?: BrandAssetUsage[];
}

interface Description {
  id: string;
  text: string;
  charCount?: number;
  brandAssetUsage?: BrandAssetUsage[];
}

interface GeneratedAssets {
  headlines: Headline[];
  descriptions: Description[];
}

interface GenerationMetadata {
  generationTime?: number;
  totalLLMCalls?: number;
  modelUsed?: string;
}

const PERSONAS = [
  { id: 'postpartum', name: '產後媽媽' },
  { id: 'weight_loss', name: '減肥瘦身' },
  { id: 'back_pain', name: '腰痠背痛' },
  { id: 'heat_sensitive', name: '怕熱女孩' },
  { id: 'premium', name: '名媛貴婦' }
] as const;

const SCENES = [
  '外出活動',
  '居家休閒',
  '居家辦公',
  '工作外勤',
  '產後照護',
  '社交聚會',
  '節慶活動',
  '辦公室工作',
  '運動健身'
] as const;

const SPECIALIZATIONS: SpecializationType[] = ['Persona特式化', '活動特式化', '見證代言特式化'];

const POLL_INTERVAL_MS = 2500;
const MAX_POLL_ATTEMPTS = 180;
const PROGRESS_STEPS: Array<{ id: V2ProgressStepId; label: string }> = [
  { id: 'input_prep', label: '整理輸入與素材' },
  { id: 'strategy', label: '規劃文案策略' },
  { id: 'creative', label: '生成 15+4 文案' },
  { id: 'judge', label: 'LLM 審稿' },
  { id: 'rewrite', label: '修正不合格句子' },
  { id: 'finalize', label: '整理最終結果' }
];

const buttonBase = {
  border: '1px solid var(--color-border)',
  borderRadius: '8px',
  padding: '0.75rem 1rem',
  background: 'var(--color-surface)',
  color: 'var(--color-text)',
  cursor: 'pointer',
  fontWeight: 700
} as const;

function FieldGroup({ label, children }: { label: string; children: ReactNode }) {
  return (
    <section style={{ display: 'grid', gap: '0.65rem' }}>
      <label style={{ fontWeight: 800 }}>{label}</label>
      {children}
    </section>
  );
}

function SelectButton({
  active,
  disabled,
  children,
  onClick
}: {
  active: boolean;
  disabled?: boolean;
  children: ReactNode;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      style={{
        ...buttonBase,
        minHeight: '2.75rem',
        borderColor: active ? 'var(--color-primary)' : 'var(--color-border)',
        background: active ? 'var(--color-primary-light)' : 'var(--color-surface)',
        color: disabled ? 'var(--color-text-tertiary)' : 'var(--color-text)',
        opacity: disabled ? 0.55 : 1,
        cursor: disabled ? 'not-allowed' : 'pointer',
        textAlign: 'left'
      }}
    >
      {children}
    </button>
  );
}

function normalizeGeneratedAssets(value: GeneratedAssets | null | undefined): GeneratedAssets | null {
  if (!value) return null;
  const headlines = Array.isArray(value.headlines) ? value.headlines : [];
  const descriptions = Array.isArray(value.descriptions) ? value.descriptions : [];
  if (headlines.length === 0 && descriptions.length === 0) return null;
  return { headlines, descriptions };
}

function toAssetText(assets: GeneratedAssets): string {
  const headlines = assets.headlines.map((item, index) => `${index + 1}. ${item.text}`).join('\n');
  const descriptions = assets.descriptions.map((item, index) => `${index + 1}. ${item.text}`).join('\n');
  return `=== Headlines (${assets.headlines.length}) ===\n${headlines}\n\n=== Descriptions (${assets.descriptions.length}) ===\n${descriptions}`;
}

function mergeUnique(values: string[], value: string): string[] {
  const trimmed = value.trim();
  if (!trimmed || values.includes(trimmed)) return values;
  return [...values, trimmed];
}

function formatDuration(totalSeconds: number): string {
  const safeSeconds = Math.max(0, Math.floor(totalSeconds));
  const minutes = Math.floor(safeSeconds / 60);
  const seconds = safeSeconds % 60;
  if (minutes <= 0) return `${seconds} 秒`;
  return `${minutes} 分 ${seconds.toString().padStart(2, '0')} 秒`;
}

function progressPercent(progress: V2ProgressUpdate | null, status: GenerationStatus): number {
  if (status === 'completed') return 100;
  if (status === 'system_fault') return 100;
  if (!progress) return status === 'queued' ? 5 : 10;
  const base = ((progress.index - 1) / progress.total) * 100;
  if (progress.status === 'completed' || progress.status === 'skipped') {
    return Math.min(96, Math.round((progress.index / progress.total) * 100));
  }
  return Math.min(96, Math.max(10, Math.round(base + 8)));
}

function stepStatus(stepId: V2ProgressStepId, progress: V2ProgressUpdate | null, status: GenerationStatus) {
  if (status === 'completed') return 'completed';
  if (status === 'system_fault') return progress?.stepId === stepId ? 'failed' : 'idle';
  if (!progress) return 'idle';
  const currentIndex = PROGRESS_STEPS.findIndex((step) => step.id === progress.stepId);
  const stepIndex = PROGRESS_STEPS.findIndex((step) => step.id === stepId);
  if (stepIndex < currentIndex) return 'completed';
  if (stepIndex > currentIndex) return 'idle';
  return progress.status;
}

function stepMarker(status: string): string {
  if (status === 'completed') return '✓';
  if (status === 'skipped') return '略過';
  if (status === 'running') return '進行中';
  if (status === 'failed') return '失敗';
  return '待處理';
}

export function SearchAdCreatorV2() {
  const pollTimerRef = useRef<number | null>(null);
  const streamRef = useRef<EventSource | null>(null);
  const pollCountRef = useRef(0);
  const pollInFlightRef = useRef(false);
  const [referenceLibrary, setReferenceLibrary] = useState<BrandReferenceLibrary | null>(null);
  const [keywordInput, setKeywordInput] = useState('');
  const [keywords, setKeywords] = useState<string[]>([]);
  const [persona, setPersona] = useState('');
  const [scene, setScene] = useState('');
  const [specialization, setSpecialization] = useState<SpecializationType>('Persona特式化');
  const [celebrityNames, setCelebrityNames] = useState<string[]>([]);
  const [activityNames, setActivityNames] = useState<string[]>([]);
  const [metaPostUrl, setMetaPostUrl] = useState('');
  const [metaPostText, setMetaPostText] = useState('');
  const [generatedAssets, setGeneratedAssets] = useState<GeneratedAssets | null>(null);
  const [metadata, setMetadata] = useState<GenerationMetadata | null>(null);
  const [generationId, setGenerationId] = useState('');
  const [generationStatus, setGenerationStatus] = useState<GenerationStatus>('queued');
  const [generationStartedAt, setGenerationStartedAt] = useState<number | null>(null);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [progress, setProgress] = useState<V2ProgressUpdate | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    void loadReferenceLibrary();
    return () => stopPolling();
  }, []);

  useEffect(() => {
    if (!loading || !generationStartedAt) return;
    const updateElapsed = () => {
      setElapsedSeconds(Math.max(0, Math.floor((Date.now() - generationStartedAt) / 1000)));
    };
    updateElapsed();
    const timer = window.setInterval(updateElapsed, 1000);
    return () => window.clearInterval(timer);
  }, [generationStartedAt, loading]);

  const activeCelebrities = referenceLibrary?.celebrities.active || [];
  const activeActivities = referenceLibrary?.activities.active || [];

  const canGenerate = useMemo(() => {
    if (loading || keywords.length === 0 || !persona) return false;
    if (specialization === '見證代言特式化' && celebrityNames.length === 0) return false;
    if (specialization === '活動特式化' && activityNames.length === 0) return false;
    return true;
  }, [activityNames.length, celebrityNames.length, keywords.length, loading, persona, specialization]);

  async function loadReferenceLibrary() {
    try {
      const library = await fetchReferenceLibrary();
      setReferenceLibrary(library);
    } catch (err) {
      setError(err instanceof Error ? err.message : '無法載入品牌素材');
    }
  }

  function stopPolling() {
    if (pollTimerRef.current !== null) {
      window.clearInterval(pollTimerRef.current);
      pollTimerRef.current = null;
    }
    streamRef.current?.close();
    streamRef.current = null;
  }

  function addKeyword() {
    setKeywords((prev) => mergeUnique(prev, keywordInput));
    setKeywordInput('');
  }

  function handleKeywordKeyDown(event: KeyboardEvent<HTMLInputElement>) {
    if (event.key === 'Enter' && !event.nativeEvent.isComposing) {
      event.preventDefault();
      addKeyword();
    }
  }

  function toggleValue(value: string, current: string[], setter: (next: string[]) => void) {
    if (current.includes(value)) {
      setter(current.filter((item) => item !== value));
      return;
    }
    setter([...current, value]);
  }

  async function pollResult(nextGenerationId: string) {
    if (pollInFlightRef.current) return;
    pollInFlightRef.current = true;
    pollCountRef.current += 1;

    try {
      const result = await getAdCreatorGenerationResultV2<GeneratedAssets, GenerationMetadata>(nextGenerationId);
      if (!result.success) {
        if (pollCountRef.current < MAX_POLL_ATTEMPTS) return;
        setError(result.error || '查詢生成結果失敗');
        setLoading(false);
        stopPolling();
        return;
      }
      if (!result.status) {
        if (pollCountRef.current < MAX_POLL_ATTEMPTS) return;
        setError('查詢生成結果失敗');
        setLoading(false);
        stopPolling();
        return;
      }

      setGenerationStatus(result.status);
      if (result.state?.progress) setProgress(result.state.progress);
      if (result.state?.startedAt) {
        const startedAtMs = Date.parse(result.state.startedAt);
        if (Number.isFinite(startedAtMs)) {
          setGenerationStartedAt(startedAtMs);
        }
      }

      const nextGenerated = normalizeGeneratedAssets(result.generated || result.state?.generated || null);
      if (nextGenerated) setGeneratedAssets(nextGenerated);
      if (result.metadata || result.state?.metadata) setMetadata(result.metadata || result.state?.metadata || null);

      if (result.status === 'completed') {
        if (!nextGenerated) {
          setError('生成已完成，但後端沒有回傳 15+4 文案');
        }
        setLoading(false);
        setElapsedSeconds(result.metadata?.generationTime || result.state?.metadata?.generationTime || elapsedSeconds);
        stopPolling();
        return;
      }

      if (result.status === 'system_fault') {
        setError(result.error || result.state?.error || '生成流程中斷');
        setLoading(false);
        stopPolling();
        return;
      }

      if (pollCountRef.current >= MAX_POLL_ATTEMPTS) {
        setError('生成逾時，後端工作仍未完成');
        setLoading(false);
        stopPolling();
        return;
      }

    } catch (err) {
      if (pollCountRef.current < MAX_POLL_ATTEMPTS) return;
      setError(err instanceof Error ? err.message : '查詢生成結果失敗');
      setLoading(false);
      stopPolling();
    } finally {
      pollInFlightRef.current = false;
    }
  }

  function startResultPolling(nextGenerationId: string) {
    if (pollTimerRef.current !== null) {
      window.clearInterval(pollTimerRef.current);
      pollTimerRef.current = null;
    }
    void pollResult(nextGenerationId);
    pollTimerRef.current = window.setInterval(() => {
      void pollResult(nextGenerationId);
    }, POLL_INTERVAL_MS);
  }

  function bindStarterStream(nextGenerationId: string) {
    streamRef.current?.close();
    const source = createAdCreatorGenerationEventSourceV2(nextGenerationId);
    streamRef.current = source;

    const handleMessage = (event: MessageEvent<string>) => {
      const payload = JSON.parse(event.data) as {
        type?: string;
        status?: GenerationStatus;
        progress?: V2ProgressUpdate;
        generated?: GeneratedAssets;
        metadata?: GenerationMetadata;
        error?: string;
      };
      if (payload.status) setGenerationStatus(payload.status);
      if (payload.progress) setProgress(payload.progress);
      const nextGenerated = normalizeGeneratedAssets(payload.generated || null);
      if (nextGenerated) setGeneratedAssets(nextGenerated);
      if (payload.metadata) setMetadata(payload.metadata);
      if (payload.type === 'completed') {
        setLoading(false);
        if (payload.metadata?.generationTime) setElapsedSeconds(payload.metadata.generationTime);
        stopPolling();
      }
      if (payload.type === 'system_fault') {
        setError(payload.error || '生成流程中斷');
        setLoading(false);
        stopPolling();
      }
    };

    source.addEventListener('generation_started', (event) => handleMessage(event as MessageEvent<string>));
    source.addEventListener('progress_updated', (event) => handleMessage(event as MessageEvent<string>));
    source.addEventListener('completed', (event) => handleMessage(event as MessageEvent<string>));
    source.addEventListener('system_fault', (event) => handleMessage(event as MessageEvent<string>));
    source.onerror = () => {
      source.close();
      if (streamRef.current === source) {
        streamRef.current = null;
      }
    };
  }

  async function generateAssets() {
    if (!canGenerate) {
      setError('請先完成關鍵字、Persona、特式化與必要素材選擇');
      return;
    }

    stopPolling();
    pollCountRef.current = 0;
    setLoading(true);
    setError('');
    setCopied(false);
    setGeneratedAssets(null);
    setMetadata(null);
    setGenerationId('');
    setGenerationStatus('queued');
    setGenerationStartedAt(Date.now());
    setElapsedSeconds(0);
    setProgress(null);

    try {
      const response = await postAdCreatorGenerateFullV2<GeneratedAssets, GenerationMetadata>({
        keywords,
        persona,
        scene: scene.trim() || undefined,
        specialization,
        celebrityNames,
        activityNames,
        metaPostUrl,
        metaPostText
      });

      if (!response.success || !response.generationId) {
        setError(response.error || '建立生成工作失敗');
        setLoading(false);
        return;
      }

      setGenerationId(response.generationId);
      setGenerationStatus(response.status);
      if (response.state?.startedAt) {
        const startedAtMs = Date.parse(response.state.startedAt);
        if (Number.isFinite(startedAtMs)) {
          setGenerationStartedAt(startedAtMs);
        }
      }

      const immediateGenerated = normalizeGeneratedAssets(response.generated || response.state?.generated || null);
      if (immediateGenerated) setGeneratedAssets(immediateGenerated);
      if (response.metadata || response.state?.metadata) setMetadata(response.metadata || response.state?.metadata || null);

      if (response.status === 'completed') {
        setLoading(false);
        if (response.metadata?.generationTime || response.state?.metadata?.generationTime) {
          setElapsedSeconds(response.metadata?.generationTime || response.state?.metadata?.generationTime || 0);
        }
        return;
      }

      bindStarterStream(response.generationId);
      startResultPolling(response.generationId);
    } catch (err) {
      setError(err instanceof Error ? err.message : '生成文案失敗');
      setLoading(false);
    }
  }

  async function copyAllAssets() {
    if (!generatedAssets) return;
    try {
      await navigator.clipboard.writeText(toAssetText(generatedAssets));
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1800);
    } catch (err) {
      setError(err instanceof Error ? err.message : '複製失敗');
    }
  }

  function resetForNewDraft() {
    stopPolling();
    setGeneratedAssets(null);
    setMetadata(null);
    setGenerationId('');
    setGenerationStatus('queued');
    setGenerationStartedAt(null);
    setElapsedSeconds(0);
    setProgress(null);
    setError('');
    setCopied(false);
    setLoading(false);
  }

  return (
    <div style={{ maxWidth: '1180px', margin: '0 auto', padding: '2rem' }}>
      <header style={{ display: 'flex', justifyContent: 'space-between', gap: '1rem', alignItems: 'center', marginBottom: '1.25rem' }}>
        <div>
          <h1 style={{ fontSize: '1.75rem', lineHeight: 1.2, margin: 0 }}>搜尋廣告文案工作台 v2</h1>
          <div style={{ color: 'var(--color-text-secondary)', marginTop: '0.35rem' }}>手動策略輸入，輸出 15 則標題與 4 則說明。</div>
        </div>
        <button type="button" onClick={resetForNewDraft} style={buttonBase}>
          清空結果
        </button>
      </header>

      {error && (
        <div style={{ border: '1px solid rgba(220,38,38,0.25)', background: '#fef2f2', color: '#991b1b', borderRadius: '8px', padding: '0.85rem 1rem', marginBottom: '1rem' }}>
          {error}
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(320px, 420px) minmax(0, 1fr)', gap: '1rem', alignItems: 'start' }}>
        <form
          onSubmit={(event) => {
            event.preventDefault();
            void generateAssets();
          }}
          style={{ border: '1px solid var(--color-border)', borderRadius: '8px', background: 'var(--color-surface)', padding: '1.25rem', display: 'grid', gap: '1.2rem' }}
        >
          <FieldGroup label="關鍵字">
            <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: '0.5rem' }}>
              <input
                value={keywordInput}
                onChange={(event) => setKeywordInput(event.target.value)}
                onKeyDown={handleKeywordKeyDown}
                placeholder="例如：塑身衣"
                style={{ border: '1px solid var(--color-border)', borderRadius: '8px', padding: '0.8rem 0.9rem', background: 'var(--color-bg)' }}
              />
              <button type="button" onClick={addKeyword} style={buttonBase}>
                新增
              </button>
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', minHeight: '2.25rem' }}>
              {keywords.map((keyword) => (
                <button
                  type="button"
                  key={keyword}
                  onClick={() => setKeywords((prev) => prev.filter((item) => item !== keyword))}
                  style={{ ...buttonBase, padding: '0.45rem 0.7rem', background: 'var(--color-primary-light)' }}
                >
                  {keyword} ×
                </button>
              ))}
            </div>
          </FieldGroup>

          <FieldGroup label="Persona">
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: '0.5rem' }}>
              {PERSONAS.map((item) => (
                <SelectButton key={item.id} active={persona === item.name} onClick={() => setPersona(item.name)}>
                  {item.name}
                </SelectButton>
              ))}
            </div>
          </FieldGroup>

          <FieldGroup label="場景">
            <input
              list="v2-scene-list"
              value={scene}
              onChange={(event) => setScene(event.target.value)}
              placeholder="可自行輸入"
              style={{ border: '1px solid var(--color-border)', borderRadius: '8px', padding: '0.8rem 0.9rem', background: 'var(--color-bg)' }}
            />
            <datalist id="v2-scene-list">
              {SCENES.map((item) => <option key={item} value={item} />)}
            </datalist>
          </FieldGroup>

          <FieldGroup label="H1 特式化">
            <div style={{ display: 'grid', gap: '0.5rem' }}>
              {SPECIALIZATIONS.map((item) => (
                <SelectButton key={item} active={specialization === item} onClick={() => setSpecialization(item)}>
                  {item}
                </SelectButton>
              ))}
            </div>
          </FieldGroup>

          <FieldGroup label="名人素材">
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', minHeight: '2.25rem' }}>
              {activeCelebrities.map((item) => (
                <SelectButton
                  key={item.name}
                  active={celebrityNames.includes(item.name)}
                  onClick={() => toggleValue(item.name, celebrityNames, setCelebrityNames)}
                >
                  {item.name}
                </SelectButton>
              ))}
              {activeCelebrities.length === 0 && <span style={{ color: 'var(--color-text-secondary)' }}>目前沒有可選名人素材</span>}
            </div>
          </FieldGroup>

          <FieldGroup label="活動素材">
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', minHeight: '2.25rem' }}>
              {activeActivities.map((item) => (
                <SelectButton
                  key={item.name}
                  active={activityNames.includes(item.name)}
                  onClick={() => toggleValue(item.name, activityNames, setActivityNames)}
                >
                  {item.name}
                </SelectButton>
              ))}
              {activeActivities.length === 0 && <span style={{ color: 'var(--color-text-secondary)' }}>目前沒有可選活動素材</span>}
            </div>
          </FieldGroup>

          <FieldGroup label="Meta 貼文網址">
            <input
              value={metaPostUrl}
              onChange={(event) => setMetaPostUrl(event.target.value)}
              placeholder="https://"
              style={{ border: '1px solid var(--color-border)', borderRadius: '8px', padding: '0.8rem 0.9rem', background: 'var(--color-bg)' }}
            />
          </FieldGroup>

          <FieldGroup label="Meta 貼文文字">
            <textarea
              value={metaPostText}
              onChange={(event) => setMetaPostText(event.target.value)}
              rows={5}
              style={{ border: '1px solid var(--color-border)', borderRadius: '8px', padding: '0.8rem 0.9rem', background: 'var(--color-bg)', resize: 'vertical' }}
            />
          </FieldGroup>

          <button
            type="submit"
            disabled={!canGenerate}
            style={{
              ...buttonBase,
              borderColor: canGenerate ? 'var(--color-primary)' : 'var(--color-border)',
              background: canGenerate ? 'var(--color-primary)' : 'var(--color-border)',
              color: 'white',
              cursor: canGenerate ? 'pointer' : 'not-allowed',
              opacity: canGenerate ? 1 : 0.65
            }}
          >
            {loading ? '生成中' : '生成 15+4'}
          </button>
        </form>

        <main style={{ display: 'grid', gap: '1rem' }}>
          {loading && (
            <div style={{ border: '1px solid var(--color-border)', borderRadius: '8px', background: 'var(--color-surface)', padding: '1.25rem' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: '1rem', alignItems: 'baseline' }}>
                <div style={{ fontWeight: 800 }}>生成中</div>
                <div style={{ color: 'var(--color-text-secondary)', fontSize: '0.9rem' }}>
                  {progressPercent(progress, generationStatus)}%
                </div>
              </div>
              <div style={{ marginTop: '0.85rem', height: '10px', borderRadius: '999px', background: 'var(--color-bg-secondary)', overflow: 'hidden' }}>
                <div
                  style={{
                    width: `${progressPercent(progress, generationStatus)}%`,
                    height: '100%',
                    borderRadius: '999px',
                    background: 'var(--color-primary)',
                    transition: 'width 0.4s ease'
                  }}
                />
              </div>
              <div style={{ display: 'grid', gap: '0.35rem', color: 'var(--color-text-secondary)', marginTop: '0.85rem', fontSize: '0.95rem' }}>
                <div>{progress?.label || (generationStatus === 'queued' ? '建立生成工作' : '等待後端進度回報')}</div>
                {progress?.detail && <div>{progress.detail}</div>}
                <div>已花時間：{formatDuration(elapsedSeconds)}｜通常約 3 到 4 分鐘</div>
                {elapsedSeconds > 240 && (
                  <div>已超過預估時間，仍會持續等待後端完成或回報中斷。</div>
                )}
                {generationId && <div>工作 ID：{generationId}</div>}
              </div>
              <div style={{ display: 'grid', gap: '0.5rem', marginTop: '1rem' }}>
                {PROGRESS_STEPS.map((step) => {
                  const currentStatus = stepStatus(step.id, progress, generationStatus);
                  const active = currentStatus === 'running';
                  const done = currentStatus === 'completed' || currentStatus === 'skipped';
                  return (
                    <div
                      key={step.id}
                      style={{
                        display: 'grid',
                        gridTemplateColumns: '4.5rem 1fr',
                        gap: '0.75rem',
                        alignItems: 'center',
                        color: done || active ? 'var(--color-text)' : 'var(--color-text-tertiary)',
                        fontSize: '0.92rem'
                      }}
                    >
                      <span
                        style={{
                          borderRadius: '999px',
                          border: `1px solid ${active ? 'var(--color-primary)' : 'var(--color-border)'}`,
                          background: done ? 'var(--color-primary-light)' : active ? 'var(--color-bg)' : 'transparent',
                          color: active ? 'var(--color-primary)' : undefined,
                          padding: '0.25rem 0.45rem',
                          textAlign: 'center',
                          fontSize: '0.78rem',
                          fontWeight: 700
                        }}
                      >
                        {stepMarker(currentStatus)}
                      </span>
                      <span>{step.label}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {generatedAssets ? (
            <>
              <section style={{ border: '1px solid var(--color-border)', borderRadius: '8px', background: 'var(--color-surface)', padding: '1.25rem' }}>
                <h2 style={{ fontSize: '1.1rem', margin: '0 0 1rem' }}>15 則標題</h2>
                <div style={{ display: 'grid', gap: '0.55rem' }}>
                  {generatedAssets.headlines.map((headline, index) => (
                    <div key={headline.id || `${headline.text}-${index}`} style={{ display: 'grid', gridTemplateColumns: '2.5rem 1fr auto', gap: '0.75rem', alignItems: 'center', border: '1px solid var(--color-border)', borderRadius: '8px', padding: '0.65rem 0.75rem' }}>
                      <strong style={{ color: 'var(--color-text-secondary)' }}>{index + 1}</strong>
                      <span>{headline.text}</span>
                      <span style={{ color: 'var(--color-text-tertiary)' }}>{headline.charCount ?? headline.text.length}</span>
                    </div>
                  ))}
                </div>
              </section>

              <section style={{ border: '1px solid var(--color-border)', borderRadius: '8px', background: 'var(--color-surface)', padding: '1.25rem' }}>
                <h2 style={{ fontSize: '1.1rem', margin: '0 0 1rem' }}>4 則說明</h2>
                <div style={{ display: 'grid', gap: '0.65rem' }}>
                  {generatedAssets.descriptions.map((description, index) => (
                    <div key={description.id || `${description.text}-${index}`} style={{ display: 'grid', gridTemplateColumns: '2.5rem 1fr auto', gap: '0.75rem', alignItems: 'start', border: '1px solid var(--color-border)', borderRadius: '8px', padding: '0.75rem' }}>
                      <strong style={{ color: 'var(--color-text-secondary)' }}>{index + 1}</strong>
                      <span>{description.text}</span>
                      <span style={{ color: 'var(--color-text-tertiary)' }}>{description.charCount ?? description.text.length}</span>
                    </div>
                  ))}
                </div>
              </section>

              <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                <button type="button" onClick={() => void generateAssets()} disabled={loading} style={buttonBase}>
                  重新生成
                </button>
                <button type="button" onClick={() => void copyAllAssets()} style={{ ...buttonBase, background: copied ? 'var(--color-primary-light)' : 'var(--color-surface)' }}>
                  {copied ? '已複製' : '複製'}
                </button>
              </div>
            </>
          ) : (
            <div style={{ border: '1px solid var(--color-border)', borderRadius: '8px', background: 'var(--color-surface)', padding: '1.25rem', color: 'var(--color-text-secondary)' }}>
              尚未生成文案
            </div>
          )}

          {metadata?.generationTime ? (
            <div style={{ display: 'none' }} aria-hidden="true">
              {metadata.generationTime}
            </div>
          ) : null}
        </main>
      </div>
    </div>
  );
}
